<template>
  <div class="alert-history">
    <b-row class="mb-4 align-items-end">
      <b-col class="text-left">
        <h1>Alert history</h1>
      </b-col>
    </b-row>
    <div class="d-flex flex-row">
      <div class="flex-grow-1">
        <b-input-group class="" prepend="Search">
          <b-form-input
            v-model="filter"
            data-cy="table-text-filter"
            type="search"
            placeholder="Type to Filter"
            @input="onFiltered"
          ></b-form-input>
        </b-input-group>
      </div>
      <b-form-group class="ml-3">
        <b-form-radio-group
          v-model="selectedStatus"
          button-variant="outline-secondary"
          buttons
          data-cy="alert-history-status-buttons"
          :options="statusOptions"
        ></b-form-radio-group>
      </b-form-group>
    </div>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <b-alert class="text-center" :show="isOutdated"
        >You have new alerts.
        <b-button
          class="ml-3"
          data-cy="alert-history-update-button"
          @click="fetchAlertsHistory"
          >Update the list</b-button
        ></b-alert
      >
      <b-list-group v-if="alerts.length > 0">
        <b-list-group-item
          v-for="alert in alerts"
          :data-cy="`Alert-${alert._id}`"
          :key="alert._id"
        >
          <b-row class="pt-1 pb-1" align-v="center">
            <b-col>
              {{
                new Date(alert._source._kuzzle_info.createdAt).toLocaleString()
              }}</b-col
            >
            <b-col>{{ alert._source.alertRule._source.name }}</b-col>
            <b-col
              v-if="alert._source.alertRule._source.collection === 'assets'"
            >
              <router-link
                :to="{
                  name: 'asset',
                  params: { assetId: alert._source.document._id }
                }"
              >
                <u>{{ alert._source.document._source.reference }}</u>
              </router-link>
            </b-col>
            <b-col v-else
              ><router-link
                :to="{
                  name: 'device',
                  params: { deviceId: alert._source.document._id }
                }"
              >
                <u>{{ alert._source.document._source.reference }}</u>
              </router-link></b-col
            >
            <b-col>
              <ul class="measurementList">
                <li
                  v-for="measurement in extractDocumentMeasurements(alert)"
                  :key="measurement.field"
                >
                  <code>{{ measurement.name }}</code> = {{ measurement.value }}
                </li>
              </ul>
            </b-col>
            <b-col>{{ alert._source.status }}</b-col>
            <b-col
              ><b-button
                v-if="alert._source.status === 'pending'"
                variant="secondary"
                :data-cy="`Alert-${alert._id}-ackBtn`"
                :disabled="!canAcknowledgeAlert"
                @click="acknowledgeAlert(alert._id)"
                ><i class="fas fa-check"></i></b-button
            ></b-col>
          </b-row>
        </b-list-group-item>
      </b-list-group>
      <b-card v-else>
        <h4 class="text-center mt-3">There is currently no alerts.</h4>
      </b-card>
      <b-pagination
        align="end"
        size="sm"
        class="mt-2"
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        aria-controls="my-table"
      ></b-pagination>
    </b-overlay>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { koncordeToConditions } from '@/ruleConditionsUtils';
import _ from 'lodash';
import { isActionAllowed } from '../helpers/rights';
import { ALERT_HISTORY_COLLECTION } from '@/helpers/alertRules';

export default {
  name: 'AlertHistory',

  data() {
    return {
      subscriptionId: null,
      isLoading: false,
      filter: null,
      currentPage: 1,
      perPage: 20,
      rows: 0,
      selectedStatus: 'pending',
      filterableFields: ['name'],
      statusOptions: [
        {
          value: 'pending',
          text: 'Pending'
        },
        {
          value: 'acknowledged',
          text: 'Acknowledged'
        }
      ],
      alerts: [],
      /**
       * Tells whether the list is out of sync with the server.
       * Manipulated by the realtime subscription.
       */
      isOutdated: false
    };
  },

  computed: {
    ...mapGetters('auth', ['currentUserRights']),
    ...mapGetters('tenants', ['selectedTenant']),
    canAcknowledgeAlert() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'update',
        this.selectedTenant.index,
        ALERT_HISTORY_COLLECTION
      );
    }
  },

  methods: {
    ...mapMutations('alerts', ['DECREMENT_PENDING']),
    async subscribe() {
      if (this.subscriptionId) {
        await this.unsubscribe();
      }
      this.$log.debug('subscribing...');
      this.subscriptionId = await this.$kuzzle.realtime.subscribe(
        this.selectedTenant.index,
        ALERT_HISTORY_COLLECTION,
        {
          equals: {
            status: 'pending'
          }
        },
        () => {
          setTimeout(() => {
            if (this.selectedStatus === 'pending') {
              this.isOutdated = true;
            }
          }, 1000);
        },
        {
          scope: 'in'
        }
      );
    },
    async unsubscribe() {
      if (!this.subscriptionId) {
        return;
      }
      await this.$kuzzle.realtime.unsubscribe(this.subscriptionId);
      this.subscriptionId = null;
    },
    async fetchAlertsHistory() {
      this.isLoading = true;
      this.$log.debug('fetching...');

      const body = {
        query: {
          bool: {
            filter: [
              {
                term: {
                  status: this.selectedStatus
                }
              }
            ],
            must: []
          }
        },
        sort: {
          '_kuzzle_info.createdAt': 'desc'
        }
      };

      if (this.filter) {
        body.query.bool.must.push({
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: this.filerableFields
          }
        });
      }

      try {
        const response = await this.$kuzzle.document.search(
          this.selectedTenant.index,
          ALERT_HISTORY_COLLECTION,
          body,
          {
            from: (this.currentPage - 1) * this.perPage,
            size: this.perPage
          }
        );

        this.rows = response.total;
        this.alerts = response.hits;
        this.isOutdated = false;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    extractDocumentMeasurements(alert) {
      const measurements = [];
      const conditions = koncordeToConditions(
        alert._source.alertRule._source.filters
      );

      for (const condition of conditions) {
        measurements.push({
          name: condition.attribute.replace('measures.', ''),
          value: _.get(
            alert._source.document._source,
            condition.attribute,
            'N/A'
          )
        });
      }

      return measurements;
    },
    async acknowledgeAlert(alertId) {
      const isAckConfirmed = await this.handleAckConfirmation();

      if (!isAckConfirmed) {
        return;
      }

      this.isLoading = true;

      try {
        await this.$kuzzle.document.update(
          this.selectedTenant.index,
          ALERT_HISTORY_COLLECTION,
          alertId,
          { status: 'acknowledged' },
          { refresh: 'wait_for' }
        );
        this.DECREMENT_PENDING();
        await this.fetchAlertsHistory();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    handleAckConfirmation() {
      return this.$bvModal.msgBoxConfirm(
        'Are you sure you want to acknowledge this alert?',
        {
          modalClass: 'Alert-ackModal'
        }
      );
    },
    onFiltered(value) {
      this.filter = value;
      this.fetchAlertsHistory();
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
        'auto-hide-delay': '10s'
      });
    }
  },

  async mounted() {
    await this.subscribe();
  },

  watch: {
    selectedTenant() {
      this.fetchAlertsHistory();
    },
    selectedStatus: {
      immediate: true,
      async handler() {
        await this.fetchAlertsHistory();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.measurementList {
  list-style: none;
  margin: 0;
  padding: 0;
}
</style>
